import { useEffect, useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { FieldGroup } from '../../../../components/inputs/FieldGroup';
import { SALUTATION_EMPTY_LABEL, SALUTATION_OPTIONS, SALUTATION_SEGMENT_WIDTH } from '../../../../lib/salutations';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { personnelApi, personsApi } from '../personnelApi';
import type { PersonnelReference } from '../personnelTypes';
import { sampleReference } from '../utils/sampleReference';

/**
 * Records or corrects one character reference.
 *
 * **The name is not on the record this form edits.** The row carries a `personId` and the name
 * lives in `rsys_pii`, so on an EDIT the form fetches it from the other system — two stores,
 * composed here, because no single endpoint can read across the seam.
 *
 * **Sending a name is not a reveal; reading one back is.** The create path posts a name the
 * reader already knows and gets an id; the edit path has to ASK for a name it does not hold,
 * which is `personnel.reveal` and is audited. That is why the fetch fires on open rather than on
 * mount — a modal nobody opened has audited nothing.
 *
 * **No date of birth is collected.** The form asks a referee for a name, an address and a
 * number. A birth date is the most sensitive of the protected fields and nothing here needs one.
 */
export function EditReferenceModal({
  personnelId,
  editing,
  onClose,
  onSaved,
}: {
  personnelId: string;
  /** Null adds one. */
  editing: PersonnelReference | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [salutation, setSalutation] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');

  const [occupation, setOccupation] = useState(editing?.occupation ?? '');
  const [company, setCompany] = useState(editing?.company ?? '');
  const [address, setAddress] = useState(editing?.address ?? '');
  const [contactNumber, setContactNumber] = useState(editing?.contactNumber ?? '');

  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (editing === null) return;

    const controller = new AbortController();
    setLoading(true);

    // The name comes from the OTHER system, and asking for it is the audited reveal.
    personsApi
      .reveal(editing.personId, controller.signal)
      .then((identity) => {
        if (controller.signal.aborted) return;

        setSalutation(identity.salutation ?? '');
        setFirstName(identity.firstName);
        setMiddleName(identity.middleName ?? '');
        setLastName(identity.lastName);
        setSuffix(identity.suffix ?? '');
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;

        setError(
          caught instanceof ApiError ? caught : new ApiError('Could not load this name.', 0),
        );

        // No focus call: a failed load names no field, so there is nothing to point at.
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [editing]);

  const valid = firstName.trim().length > 0 && lastName.trim().length > 0;
  const canSave = valid && confirmed && !saving && !loading;

  /** Fills every required field, so the primary action enables on the first try. */
  function generate() {
    const sample = sampleReference();

    setSalutation(sample.salutation);
    setFirstName(sample.firstName);
    setMiddleName(sample.middleName);
    setLastName(sample.lastName);
    setSuffix(sample.suffix);
    setOccupation(sample.occupation);
    setCompany(sample.company);
    setAddress(sample.address);
    setContactNumber(sample.contactNumber);
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      salutation: salutation.trim() ? salutation.trim() : null,
      firstName: firstName.trim(),
      middleName: middleName.trim() || null,
      lastName: lastName.trim(),
      suffix: suffix.trim() || null,

      // Empty is ABSENT, not a blank value — one meaning for the null downstream.
      occupation: occupation.trim() || null,
      company: company.trim() || null,
      address: address.trim() || null,
      contactNumber: contactNumber.trim() || null,
    };

    try {
      if (editing) await personnelApi.updateReference(personnelId, editing.id, request);
      else await personnelApi.addReference(personnelId, request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this record.', 0);

      setError(failure);

      /*
        Every server field name kebabs straight onto an input id here — `contactNumber` becomes
        `reference-contact-number` — so no `fieldIds` map is needed. The `<FieldGroup>` keeps a
        distinct id per segment for exactly this reason: a joined control must not collapse two
        fields into one the DOM cannot tell apart.
      */
      focusValidationError(failure, { idPrefix: 'reference' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit reference' : 'Add a reference'}
      size="2xl"
      id="reference-modal"
      autogenerate={{ onGenerate: generate, disabled: saving || loading }}
      confirm={{
        id: 'reference-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="reference-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="reference-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/*
          THE NAME ROW — three equal columns, which is the one exception to the two-column grid:
          first, middle and last are a single fact split across three controls, so they read as
          one line rather than wrapping.

          These three go to `rsys_pii` and nothing else on this form does.
        */}
        <div className="grid gap-4 sm:grid-cols-3">
          <FieldGroup
            label="First name"
            labelFor="reference-first-name"
            description="The narrow box on the left is a title — Mr., Dr., Atty. It is optional, and it is not part of the name."
            segments={[
              {
                id: 'reference-salutation',
                label: 'Salutation',
                value: salutation,
                onChange: setSalutation,
                options: SALUTATION_OPTIONS,
                allowCustom: true,
                error: error?.fieldError('salutation'),
                disabled: saving,
                // The addon gives way, never the grid — a name row is three equal columns.
                width: SALUTATION_SEGMENT_WIDTH,
                emptyLabel: SALUTATION_EMPTY_LABEL,
              },
              {
                id: 'reference-first-name',
                label: 'First name',
                value: firstName,
                onChange: setFirstName,
                required: true,
                error: firstName.trim() ? error?.fieldError('firstName') : 'Enter a first name.',
                disabled: saving,
              },
            ]}
          />

          <TextField
            id="reference-middle-name"
            label="Middle name"
            description="Optional — plenty of records have none."
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            error={error?.fieldError('middleName')}
            disabled={saving || loading}
          />

          <FieldGroup
            label="Last name"
            description="The narrow box is the form’s name extension — Jr., Sr., III."
            segments={[
              {
                id: 'reference-last-name',
                label: 'Last name',
                value: lastName,
                onChange: setLastName,
                required: true,
                error: lastName.trim() ? error?.fieldError('lastName') : 'Enter a surname.',
                disabled: saving || loading,
              },
              {
                id: 'reference-suffix',
                label: 'Suffix',
                value: suffix,
                onChange: setSuffix,
                error: error?.fieldError('suffix'),
                disabled: saving || loading,
                width: 'w-20 shrink-0',
              },
            ]}
          />
        </div>

        {/*
          The LOCAL half — what the referee does, who for, and how to reach them. None of this
          leaves `rsys_`, and all of it is optional: a referee somebody can name without being
          able to state their job title is still a referee.
        */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="reference-occupation"
            label="Occupation"
            description="What they do — their position or profession."
            value={occupation}
            onChange={(e) => setOccupation(e.target.value)}
            error={error?.fieldError('occupation')}
            disabled={saving || loading}
          />

          <TextField
            id="reference-company"
            label="Company"
            description="The organization they are affiliated with."
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            error={error?.fieldError('company')}
            disabled={saving || loading}
          />

          <TextField
            id="reference-contact-number"
            label="Contact number"
            description="How to reach them. Leave blank if nobody recorded it."
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value)}
            error={error?.fieldError('contactNumber')}
            disabled={saving || loading}
          />
        </div>

        {/*
          FULL WIDTH, outside the grid — an address is the longest value on this form, so half a
          row is the box that truncates while the field beside it needed none of the room.

          ONE free-text line rather than the four address parts an emergency contact carries: the
          form prints a single ADDRESS box for a referee, and the honest answer to a one-box
          question is one box.
        */}
        <TextField
          id="reference-address"
          label="Address"
          description="Where they are. One line, written as they give it."
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          error={error?.fieldError('address')}
          disabled={saving || loading}
        />
      </div>
    </Modal>
  );
}
